<template>
  <div class="statistics-dashboard" v-if="devicesStatus && devicesStatus.length > 0">
    <div class="stat-card total-card">
      <div class="stat-icon total-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
        </svg>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ totalDevices }}</div>
        <div class="stat-label">{{ $t('total_devices') }}</div>
      </div>
    </div>

    <div class="stat-card working-card" @click="$emit('show-working')">
      <div class="stat-icon working-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
        </svg>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ workingDevices }}</div>
        <div class="stat-label">{{ $t('working') }}</div>
        <div class="stat-percentage working-percentage">{{ workingPercentage }}%</div>
      </div>
      <div class="stat-progress-bar">
        <div class="stat-progress-fill working-fill" :style="{ width: `${workingPercentage}%` }"></div>
      </div>
    </div>

    <div class="stat-card warning-card">
      <div class="stat-icon warning-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" fill="currentColor"/>
        </svg>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ warningDevices }}</div>
        <div class="stat-label">{{ $t('warning') }}</div>
        <div class="stat-percentage warning-percentage">{{ warningPercentage }}%</div>
      </div>
      <div class="stat-progress-bar">
        <div class="stat-progress-fill warning-fill" :style="{ width: `${warningPercentage}%` }"></div>
      </div>
    </div>

    <div class="stat-card offline-card" @click="$emit('show-offline')">
      <div class="stat-icon offline-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor"/>
        </svg>
      </div>
      <div class="stat-content">
        <div class="stat-value">{{ offlineDevices }}</div>
        <div class="stat-label">{{ $t('offline') }}</div>
        <div class="stat-percentage offline-percentage">{{ offlinePercentage }}%</div>
      </div>
      <div class="stat-progress-bar">
        <div class="stat-progress-fill offline-fill" :style="{ width: `${offlinePercentage}%` }"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DeviceStatusStats',
  props: {
    devicesStatus: { type: Array, required: true },
    totalDevices: { type: Number, required: true },
    workingDevices: { type: Number, required: true },
    warningDevices: { type: Number, required: true },
    offlineDevices: { type: Number, required: true },
    workingPercentage: { type: Number, required: true },
    warningPercentage: { type: Number, required: true },
    offlinePercentage: { type: Number, required: true }
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.statistics-dashboard {
  padding: 30px 25px 25px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 25px;

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 20px 15px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 15px;
    margin-bottom: 15px;
  }

  .stat-card {
    background: $white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    gap: 15px;
    transition: transform 0.2s, box-shadow 0.2s;
    position: relative;
    overflow: hidden;

    @media (max-width: 820px) {
      padding: 15px;
      gap: 12px;
      overflow: visible;
    }

    @media (max-width: 480px) {
      padding: 12px;
      gap: 10px;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

      @media (max-width: 820px) {
        transform: none;
      }
    }

    .stat-icon {
      width: 48px;
      height: 48px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      @media (max-width: 820px) {
        width: 40px;
        height: 40px;
        border-radius: 10px;
      }

      @media (max-width: 480px) {
        width: 36px;
        height: 36px;
        border-radius: 8px;
      }

      svg {
        width: 24px;
        height: 24px;

        @media (max-width: 820px) {
          width: 20px;
          height: 20px;
        }

        @media (max-width: 480px) {
          width: 18px;
          height: 18px;
        }
      }
    }

    .stat-content {
      flex: 1;
      min-width: 0;

      .stat-value {
        font-size: 28px;
        font-weight: $font-weight-bold;
        color: $black-blue;
        line-height: 1.2;
        margin-bottom: 4px;

        @media (max-width: 820px) {
          font-size: 24px;
        }

        @media (max-width: 480px) {
          font-size: 20px;
        }
      }

      .stat-label {
        font-size: $font-size-small;
        color: $dark-grey;
        margin-bottom: 6px;
        font-weight: $font-weight-regular;

        @media (max-width: 480px) {
          font-size: 12px;
          margin-bottom: 4px;
        }
      }

      .stat-percentage {
        font-size: $font-size-small;
        font-weight: $font-weight-semi-bold;
        margin-top: 4px;

        @media (max-width: 480px) {
          font-size: 11px;
        }
      }
    }

    .stat-progress-bar {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: $light-grey;
      overflow: hidden;

      .stat-progress-fill {
        height: 100%;
        transition: width 0.3s ease;
      }
    }

    &.total-card {
      .total-icon {
        background: linear-gradient(135deg, $blue 0%, $deep-sky-blue 100%);
        color: $white;
      }
    }

    &.working-card {
      cursor: pointer;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }

      .working-icon {
        background: linear-gradient(135deg, $darker-green 0%, $green 100%);
        color: $white;
      }

      .working-percentage {
        color: $darker-green;
      }

      .working-fill {
        background: linear-gradient(90deg, $darker-green 0%, $green 100%);
      }
    }

    &.warning-card {
      .warning-icon {
        background: linear-gradient(135deg, $orange 0%, $saffron 100%);
        color: $white;
      }

      .warning-percentage {
        color: $orange;
      }

      .warning-fill {
        background: linear-gradient(90deg, $orange 0%, $saffron 100%);
      }
    }

    &.offline-card {
      cursor: pointer;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }

      .offline-icon {
        background: linear-gradient(135deg, $red 0%, $persian-red 100%);
        color: $white;
      }

      .offline-percentage {
        color: $red;
      }

      .offline-fill {
        background: linear-gradient(90deg, $red 0%, $persian-red 100%);
      }
    }
  }
}
</style>

